/* Dual-handle price slider (design 02 filter drawer).

   A thin UI layer over Dawn's price-range: it reads and writes the existing
   "From"/"To" number inputs, so the native facet mechanism (validation, URL
   sync, Section Rendering re-render) keeps working untouched. The number inputs
   are visually hidden in the drawer; the two range handles carry the keyboard
   affordance (arrow keys), and a live ₹ readout sits beneath.

   Markup (rendered by snippets/price-facet.liquid, inside <price-range>):
     <kashira-price-slider>
       <div class="kashira-price-slider__track">
         <div class="kashira-price-slider__fill"></div>
         <input type="range" class="…__input …__input--min">
         <input type="range" class="…__input …__input--max">
       </div>
       <div class="kashira-price-slider__readout">
         <span class="…__value--min"></span><span class="…__value--max"></span>
       </div>
     </kashira-price-slider>
*/
if (!customElements.get('kashira-price-slider')) {
  class KashiraPriceSlider extends HTMLElement {
    connectedCallback() {
      const range = this.closest('price-range');
      if (!range) return;
      const fields = range.querySelectorAll('.field__input');
      this.fromInput = fields[0];
      this.toInput = fields[1];
      this.minRange = this.querySelector('.kashira-price-slider__input--min');
      this.maxRange = this.querySelector('.kashira-price-slider__input--max');
      this.fill = this.querySelector('.kashira-price-slider__fill');
      this.minReadout = this.querySelector('.kashira-price-slider__value--min');
      this.maxReadout = this.querySelector('.kashira-price-slider__value--max');
      if (!this.fromInput || !this.toInput || !this.minRange || !this.maxRange) return;

      this.max = this.num(this.fromInput.dataset.max) || 0;
      this.min = this.num(this.fromInput.dataset.min) || 0;
      if (this.max <= this.min) return;

      // 1% step keeps drags smooth; the applied value rounds to whole rupees.
      const step = Math.max(1, Math.round((this.max - this.min) / 100));
      [this.minRange, this.maxRange].forEach((r) => {
        r.min = this.min;
        r.max = this.max;
        r.step = step;
      });

      this.syncFromInputs();

      // The range inputs sit INSIDE the facets form, so their native input/change
      // events would bubble to FacetFiltersForm's debounced submit and fire a
      // re-render with the From/To inputs still empty. Stop them here — only the
      // commit (which writes the From/To inputs) should trigger the facet update.
      // Live: update fill + readout while dragging; clamp handles so they can't cross.
      this.minRange.addEventListener('input', (e) => { e.stopPropagation(); this.onDrag('min'); });
      this.maxRange.addEventListener('input', (e) => { e.stopPropagation(); this.onDrag('max'); });
      // Commit: write to the number inputs and fire the facet update on release.
      this.minRange.addEventListener('change', (e) => { e.stopPropagation(); this.commit(); });
      this.maxRange.addEventListener('change', (e) => { e.stopPropagation(); this.commit(); });
      // If the (hidden) number inputs change some other way, mirror the handles.
      this.fromInput.addEventListener('change', () => this.syncFromInputs());
      this.toInput.addEventListener('change', () => this.syncFromInputs());
    }

    num(v) {
      if (v == null) return NaN;
      return parseFloat(String(v).replace(/[^\d.]/g, ''));
    }

    format(v) {
      return '₹' + Math.round(v).toLocaleString('en-IN');
    }

    syncFromInputs() {
      const from = this.num(this.fromInput.value);
      const to = this.num(this.toInput.value);
      this.minRange.value = isNaN(from) ? this.min : Math.max(this.min, Math.min(from, this.max));
      this.maxRange.value = isNaN(to) ? this.max : Math.max(this.min, Math.min(to, this.max));
      this.paint();
    }

    onDrag(which) {
      let lo = parseFloat(this.minRange.value);
      let hi = parseFloat(this.maxRange.value);
      if (lo > hi) {
        // push the other handle so they never invert
        if (which === 'min') this.maxRange.value = lo;
        else this.minRange.value = hi;
      }
      this.paint();
    }

    paint() {
      const lo = parseFloat(this.minRange.value);
      const hi = parseFloat(this.maxRange.value);
      const span = this.max - this.min || 1;
      const loPct = ((lo - this.min) / span) * 100;
      const hiPct = ((hi - this.min) / span) * 100;
      this.fill.style.left = loPct + '%';
      this.fill.style.right = 100 - hiPct + '%';
      if (this.minReadout) this.minReadout.textContent = this.format(lo);
      if (this.maxReadout) this.maxReadout.textContent = this.format(hi);
    }

    commit() {
      const lo = Math.round(parseFloat(this.minRange.value));
      const hi = Math.round(parseFloat(this.maxRange.value));
      // Leave a bound empty when it's at the extreme, so we don't pin a filter
      // that isn't really constraining the range.
      this.setInput(this.fromInput, lo <= this.min ? '' : lo);
      this.setInput(this.toInput, hi >= this.max ? '' : hi);
    }

    setInput(input, value) {
      if (String(input.value) === String(value)) return;
      input.value = value;
      // FacetFiltersForm binds a debounced handler to the form's `input` event;
      // price-range.js validates on `change`. Fire both (bubbling) so the facet
      // re-render triggers and the min/max stay valid.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('kashira-price-slider', KashiraPriceSlider);
}
